import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, BookOpen, ArrowRight } from 'lucide-react';
import { API_URL as API } from '@/utils/apiConfig';

import { homeTarget, ShareHomeButton } from '@/components/ShareHomeButton';
// Public landing for a shared end-of-plan recap: /share/plan-recap/:shareId
export default function SharedPlanRecapPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    axios.get(`${API}/plan-recap/share/${shareId}`)
      .then((r) => { setDoc(r.data); setState('ready'); })
      .catch(() => setState('error'));
  }, [shareId]);

  const shell = 'page-container';
  const shellStyle = { background: '#0f172a', color: '#f1f5f9' };

  if (state === 'loading') {
    return (
      <div className={`${shell} px-5 items-center justify-center`} style={shellStyle} data-testid="shared-plan-recap-loading">
        <div className="w-8 h-8 border-2 border-t-transparent border-indigo-400 rounded-full animate-spin" />
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className={`${shell} px-5 items-center justify-center text-center`} style={shellStyle} data-testid="shared-plan-recap-error">
        <p className="text-lg font-semibold mb-2">Recap unavailable</p>
        <p className="text-sm opacity-70 mb-6">This link may have expired.</p>
        <button onClick={() => navigate(homeTarget(), { replace: true })} className="px-5 py-2.5 rounded-full text-sm font-medium" style={{ background: '#6366f1', color: '#fff' }}>Open Eili</button>
      </div>
    );
  }

  const who = doc.sharer_first_name ? `${doc.sharer_first_name} finished` : 'Someone finished';

  return (
    <div className={shell} style={shellStyle} data-testid="shared-plan-recap-page">
      <ShareHomeButton />
      <div className="page-content px-5 py-10"><div className="max-w-xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider mb-4" style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}>
          <Sparkles className="h-3.5 w-3.5" /> PLAN RECAP
        </div>
        <p className="text-sm opacity-70 mb-2" data-testid="shared-plan-recap-meta">
          {who} “{doc.plan_title}” · {doc.length_days}-day plan
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4" data-testid="shared-plan-recap-headline">{doc.headline}</h1>
        {doc.summary && <p className="text-[15px] leading-relaxed opacity-80 mb-8">{doc.summary}</p>}

        <p className="text-[11px] font-semibold tracking-wider mb-3" style={{ color: '#a5b4fc' }}>KEY TAKEAWAYS</p>
        <ol className="space-y-3 mb-8" data-testid="shared-plan-recap-takeaways">
          {doc.takeaways.map((tk, i) => (
            <li key={i} className="flex gap-3 rounded-2xl p-4" style={{ background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(148,163,184,0.15)' }}>
              <span className="h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: '#818cf8', color: '#0f172a' }}>{i + 1}</span>
              <p className="text-[14px] leading-relaxed">{tk}</p>
            </li>
          ))}
        </ol>

        {doc.themes?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {doc.themes.map((th, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-full" style={{ background: 'rgba(99,102,241,0.15)', color: '#c7d2fe' }}>{th}</span>
            ))}
          </div>
        )}

        <div className="rounded-3xl p-6" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(30,41,59,0.8))', border: '1px solid rgba(129,140,248,0.35)' }}>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-4 w-4" style={{ color: '#a5b4fc' }} />
            <p className="text-sm font-semibold">Study it yourself</p>
          </div>
          <p className="text-sm opacity-75 mb-5">Get your own copy of this {doc.length_days}-day plan, guided day by day by Eili.</p>
          <button
            onClick={() => navigate(`/plans/join/${doc.share_code}`)}
            className="group w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold transition-transform hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: '#6366f1', color: '#fff' }}
            data-testid="shared-plan-recap-start-btn"
          >
            Start this plan with Eili
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
